// Editor controlado de "tarifas por comuna" (override de zona). Comparten esta UX la
// configuración del operador (DriverPaymentSection) y el override por repartidor
// (DriverTariffs). Ambos escriben el MISMO shape RELATIVO:
//   { communeNames: [comuna], adjustmentType: 'add' | 'subtract', amount }
// El override SUMA o DESCUENTA un monto fijo sobre la tarifa base por defecto (ya no la
// reemplaza). La comuna se elige con un selector Región → Comuna (una comuna por regla),
// evitando el texto libre.
//
// CATÁLOGO: los selectores se alimentan del módulo `geo` del BFF (`regiones.json` /
// `comunas_by_region.json`), NO de `contracts/countries/*`. La razón es el filtrado por
// cobertura: `logisticscompanies.coverageAreas` está expresada con los ObjectIds de
// `countries.regions[]._id` y `countries.regions[].communes[]._id`, y `geo` es el único
// módulo que los expone (el de contracts declara los subdocumentos con
// `@Schema({ _id: false })`, así que devuelve solo `name` y `code`). Con `geo` el cruce
// es por id y no hay que intersectar por nombre normalizado. Ambos módulos leen la misma
// colección, así que los nombres que ve el operador son los mismos de antes.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, Globe, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import SearchDropdown from "../../../../components/searchDropdown";
import { getRegions, getCommunesByRegion } from "../../../../api/settings";
import { useCountry } from "../../../../context/CountryContext";
import { pluralize } from "../../../../utils/divisionLabels";
import {
  DRIVER_VEHICLE_ORDER,
  DRIVER_VEHICLE_OPTIONS,
  driverVehicleLabel,
} from "../../../../utils/driverVehicleTypes";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

// Ruta de la pestaña donde el operador declara dónde opera. El editor enlaza a ella
// cuando no hay cobertura: sin eso el dropdown queda vacío y mudo.
const COVERAGE_TAB_PATH = "/admin/settings?tab=coverage";

// Caches a nivel de módulo (persisten entre montajes del editor en la misma sesión).
// Guardan el catálogo CRUDO (sin filtrar por cobertura): el filtro se aplica al usarlo,
// así un cambio de cobertura se refleja sin invalidar el cache.
// Las regiones se particionan por país; las comunas se indexan por `regionId`, que ya es
// un ObjectId único a nivel global.
const regionsCacheByCountry = new Map(); // countryId -> [{ value: regionId, label: name }]
const communesCacheByRegion = new Map(); // regionId -> [{ value: name, label: name, id: communeId }]

let _uidSeq = 0;
const uid = () => `z${++_uidSeq}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Normalización de comuna espejo del backend (NFD, sin diacríticos, minúsculas, trim).
const normalizeName = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Las comunas se guardan NORMALIZADAS (minúsculas) en el backend; se muestran capitalizadas.
const titleCase = (s) =>
  String(s || "").replace(/\b\p{L}/gu, (c) => c.toUpperCase());

// API -> filas de UI. Expande cada zona (una fila por comuna) y traduce el shape:
//   - relativo (adjustmentType + amount): se usa tal cual.
//   - legacy (baseCost absoluto): se migra a su ajuste equivalente respecto de `def`
//     (la tarifa base por defecto aplicable), para mostrarse como suma/descuento.
// `regionId` queda vacío: se reconstruye al montar (ver hidratación) a partir del nombre.
export const zonesFromApi = (apiZones, defaultBaseCost) => {
  const def = Math.round(num(defaultBaseCost));
  const rows = [];
  (Array.isArray(apiZones) ? apiZones : []).forEach((z) => {
    const communes = Array.isArray(z.communeNames) ? z.communeNames : [];
    // Filtro opcional de vehículos: se pasa tal cual (ausente/[] = todos). Sin el
    // modo por vehículo el editor lo ignora, así el shape queda idéntico.
    const vehicleTypes = Array.isArray(z.vehicleTypes) ? z.vehicleTypes : [];
    communes.forEach((commune) => {
      if (!commune) return;
      if (z.adjustmentType === "add" || z.adjustmentType === "subtract") {
        rows.push({
          id: uid(),
          regionId: "",
          commune,
          adjustmentType: z.adjustmentType,
          amount: z.amount != null ? String(z.amount) : "",
          vehicleTypes,
        });
      } else if (z.baseCost != null) {
        const delta = Math.round(num(z.baseCost)) - def;
        rows.push({
          id: uid(),
          regionId: "",
          commune,
          adjustmentType: delta < 0 ? "subtract" : "add",
          amount: String(Math.abs(delta)),
          vehicleTypes,
        });
      }
    });
  });
  return rows;
};

// Filas de UI -> API. Una comuna por fila; descarta filas sin comuna o con monto <= 0.
// `vehicleTypes` se incluye SOLO cuando la fila tiene vehículos específicos; vacío se
// omite (regla genérica), dejando el payload byte-idéntico al de sin filtro por vehículo.
//
// OJO: acá NO se filtra por cobertura a propósito. Una regla vieja cuya comuna quedó
// fuera de la cobertura actual se sigue enviando tal cual; descartarla sería borrarle al
// operador una tarifa vigente sin decírselo. El editor la marca en pantalla para que la
// decisión de quitarla sea suya.
export const zonesToApi = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((r) => {
      // El trim va ANTES de decidir si la fila tiene comuna: una comuna de solo espacios
      // producía `communeNames: [""]`, que el backend descartaba igual pero dejaba el
      // payload mintiendo (y no coincidía con `countIncompleteZoneRows`).
      const commune = String(r.commune ?? "").trim();
      const z = {
        communeNames: commune ? [commune] : [],
        adjustmentType: r.adjustmentType === "subtract" ? "subtract" : "add",
        amount: Math.max(0, Math.round(num(r.amount))),
      };
      if (Array.isArray(r.vehicleTypes) && r.vehicleTypes.length > 0) {
        z.vehicleTypes = r.vehicleTypes;
      }
      return z;
    })
    .filter((z) => z.communeNames.length > 0 && z.amount > 0);

/**
 * Filas que `zonesToApi` descartaría en silencio: sin comuna seleccionada o con monto
 * <= 0. Alimenta el aviso previo al guardado — para el operador, una fila descartada sin
 * decir nada es indistinguible de una tarifa guardada, y son montos que se liquidan.
 */
export const countIncompleteZoneRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(
    (r) => !String(r.commune ?? "").trim() || Math.round(num(r.amount)) <= 0,
  ).length;

/**
 * `coverageAreas` -> Map(regionId -> Set(communeId)).
 * Convención del backend (contracts.service: "communeIds vacío significa región
 * completa"): un `Set` vacío habilita TODAS las comunas de esa región.
 */
const buildCoverageMap = (coverageAreas) => {
  const map = new Map();
  (Array.isArray(coverageAreas) ? coverageAreas : []).forEach((area) => {
    if (!area?.regionId) return;
    const ids = (Array.isArray(area.communeIds) ? area.communeIds : []).map(String);
    map.set(String(area.regionId), new Set(ids));
  });
  return map;
};

export default function ComunaZonesEditor({
  rows,
  onChange,
  currency = "CLP",
  baseCost,
  // Modo "diferenciado por vehículo": muestra un selector compacto de vehículos por
  // fila y calcula el preview por vehículo. Sin esta prop el editor es idéntico al previo.
  vehicleFilter = false,
  // Base efectiva por vehículo ({ camion, furgo, sedan, moto }) para el preview por vehículo.
  vehicleBaseCosts = null,
  // Cobertura declarada por el operador en /admin/settings?tab=coverage, tal cual viene
  // en `logisticscompanies.coverageAreas`: [{ regionId, communeIds }] con ObjectIds.
  //   - Array (aunque sea vacío) = cobertura CONOCIDA.
  //   - null/undefined = todavía no se sabe (no cargó o falló): el editor no filtra ni
  //     marca nada, solo impide agregar reglas nuevas. Asumir "sin cobertura" ante un
  //     error de red haría parecer inválidas reglas que sí lo son.
  coverageAreas = null,
  coverageLoading = false,
}) {
  const { t } = useTranslation();
  // Rótulos de la división territorial según el país del operador ("Región"/"Comuna" en
  // Chile, "Provincia"/"Municipio" en Argentina). Mismo patrón que CoverageTab: el rótulo
  // sale del catálogo de países y se interpola en las frases traducibles.
  const { countryId, locale, labels, loading: countryLoading } = useCountry();
  const regionWord = labels?.region ?? "Región";
  const communeWord = labels?.commune ?? "Comuna";
  const regionLower = regionWord.toLowerCase();
  const communeLower = communeWord.toLowerCase();

  const [regions, setRegions] = useState(() => regionsCacheByCountry.get(countryId) ?? []);
  const [communesByRegion, setCommunesByRegion] = useState({});
  // Estado de carga por región: 'loading' | 'loaded' | 'error'.
  const [communeStatus, setCommuneStatus] = useState({});
  const [loadingRegions, setLoadingRegions] = useState(false);
  const inFlight = useRef(new Set());

  // Ref siempre con las filas más recientes: evita pisar ediciones concurrentes durante la
  // hidratación asíncrona.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const coverage = useMemo(() => buildCoverageMap(coverageAreas), [coverageAreas]);
  const coverageKnown = !coverageLoading && Array.isArray(coverageAreas);
  const noCoverage = coverageKnown && coverage.size === 0;

  // Carga de regiones (una vez por país, cacheada a nivel de módulo).
  useEffect(() => {
    if (countryLoading || !countryId) return undefined;
    const cached = regionsCacheByCountry.get(countryId);
    if (cached) {
      setRegions(cached);
      return undefined;
    }
    let alive = true;
    setLoadingRegions(true);
    getRegions(countryId)
      .then((res) => {
        const data = res?.data ?? res;
        if (!alive || !Array.isArray(data)) return;
        const opts = data.map((r) => ({ value: String(r._id), label: r.name }));
        regionsCacheByCountry.set(countryId, opts);
        setRegions(opts);
      })
      .catch(() => {})
      .finally(() => alive && setLoadingRegions(false));
    return () => {
      alive = false;
    };
  }, [countryId, countryLoading]);

  // Carga (idempotente) de las comunas de una región. Registra estado explícito para no
  // quedar en "Cargando…" para siempre si la request falla.
  const ensureCommunes = useCallback(async (regionId) => {
    if (!regionId) return;
    const cached = communesCacheByRegion.get(regionId);
    if (cached) {
      setCommunesByRegion((m) => (m[regionId] ? m : { ...m, [regionId]: cached }));
      setCommuneStatus((s) => (s[regionId] === "loaded" ? s : { ...s, [regionId]: "loaded" }));
      return;
    }
    if (inFlight.current.has(regionId)) return;
    inFlight.current.add(regionId);
    setCommuneStatus((s) => ({ ...s, [regionId]: "loading" }));
    try {
      const res = await getCommunesByRegion(regionId);
      const data = res?.data ?? res;
      const communes = data?.communes;
      if (Array.isArray(communes)) {
        const opts = communes.map((c) => ({
          value: c.name,
          label: c.name,
          id: String(c._id),
        }));
        communesCacheByRegion.set(regionId, opts);
        setCommunesByRegion((m) => ({ ...m, [regionId]: opts }));
        setCommuneStatus((s) => ({ ...s, [regionId]: "loaded" }));
      } else {
        setCommuneStatus((s) => ({ ...s, [regionId]: "error" }));
      }
    } catch {
      setCommuneStatus((s) => ({ ...s, [regionId]: "error" }));
    } finally {
      inFlight.current.delete(regionId);
    }
  }, []);

  // Solo las regiones con cobertura declarada llegan al selector.
  const regionOptions = useMemo(
    () => regions.filter((r) => coverage.has(r.value)),
    [regions, coverage],
  );

  // Comunas seleccionables de una región: las declaradas en la cobertura, o todas si la
  // región está cubierta completa (`communeIds: []`).
  const communeOptions = useCallback(
    (regionId) => {
      const all = communesByRegion[regionId] ?? [];
      const allowed = coverage.get(regionId);
      if (!allowed || allowed.size === 0) return all;
      return all.filter((c) => allowed.has(c.id));
    },
    [communesByRegion, coverage],
  );

  // Nombres (normalizados) que el operador PUEDE elegir hoy. Es el conjunto contra el que
  // se detectan las reglas heredadas que quedaron fuera de cobertura.
  const coveredCommuneNames = useMemo(() => {
    const set = new Set();
    regionOptions.forEach((r) => {
      communeOptions(r.value).forEach((c) => set.add(normalizeName(c.value)));
    });
    return set;
  }, [regionOptions, communeOptions]);

  // ¿Se puede afirmar que una comuna está fuera de cobertura? Solo cuando el catálogo de
  // TODAS las regiones cubiertas cargó bien. Con una carga a medias marcaríamos como
  // "fuera de cobertura" comunas que sí lo están.
  const coverageResolved =
    noCoverage ||
    (coverageKnown &&
      regionOptions.length > 0 &&
      regionOptions.every((r) => communeStatus[r.value] === "loaded"));

  const isOutOfCoverage = (row) =>
    coverageResolved &&
    !!String(row.commune ?? "").trim() &&
    !coveredCommuneNames.has(normalizeName(row.commune));

  // Reconstrucción de `regionId` al montar con filas que traen comuna pero no región
  // (recién cargadas de la API): se cargan las comunas de todas las regiones CUBIERTAS
  // para mapear comuna→región. El mismo barrido deja el catálogo completo, que es lo que
  // permite detectar las reglas fuera de cobertura.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !coverageKnown || !countryId) return undefined;
    if (regionOptions.length === 0) return undefined;
    if (!(rowsRef.current ?? []).some((r) => r.commune)) return undefined;
    hydratedRef.current = true;
    let alive = true;
    (async () => {
      await Promise.all(regionOptions.map((r) => ensureCommunes(r.value)));
      if (!alive) return;
      // Se construye desde el cache de módulo (actualizado de forma síncrona por
      // `ensureCommunes`) y SOLO con las comunas seleccionables: asignarle su región a
      // una comuna fuera de cobertura dejaría la fila con un selector que no la contiene.
      const nameToRegion = new Map();
      regionOptions.forEach((r) => {
        const allowed = coverage.get(r.value);
        (communesCacheByRegion.get(r.value) ?? []).forEach((c) => {
          if (allowed && allowed.size > 0 && !allowed.has(c.id)) return;
          const key = normalizeName(c.value);
          if (!nameToRegion.has(key)) nameToRegion.set(key, r.value);
        });
      });
      const current = rowsRef.current ?? [];
      const updated = current.map((r) =>
        r.commune && !r.regionId
          ? { ...r, regionId: nameToRegion.get(normalizeName(r.commune)) || "" }
          : r,
      );
      if (updated.some((r, i) => r.regionId !== (current[i]?.regionId ?? ""))) {
        onChange(updated);
      }
    })();
    return () => {
      alive = false;
    };
  }, [coverageKnown, regionOptions, coverage, ensureCommunes, onChange, countryId]);

  const addRow = () =>
    onChange([
      ...(rows ?? []),
      { id: uid(), regionId: "", commune: "", adjustmentType: "add", amount: "", vehicleTypes: [] },
    ]);
  const setRow = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  // Alterna un vehículo en el filtro de la fila. "Todos" = arreglo vacío.
  const toggleVehicle = (i, v) =>
    setRow(i, (() => {
      const cur = Array.isArray(rows[i]?.vehicleTypes) ? rows[i].vehicleTypes : [];
      const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
      // Reordena al orden canónico para un guardado estable.
      return { vehicleTypes: DRIVER_VEHICLE_ORDER.filter((x) => next.includes(x)) };
    })());
  const clearVehicles = (i) => setRow(i, { vehicleTypes: [] });

  const onRegionChange = (i, regionId) => {
    setRow(i, { regionId, commune: "" });
    ensureCommunes(regionId);
  };

  const regionLabel = (regionId) =>
    regionOptions.find((r) => r.value === regionId)?.label || "";

  const communePlaceholder = (row) => {
    if (!row.regionId) return t("Primero elige {{region}}", { region: regionLower });
    const st = communeStatus[row.regionId];
    if (st === "loading") return t("Cargando…");
    if (st === "error") return t("Error al cargar; reintenta");
    return t("Seleccione…");
  };

  const def = Math.round(num(baseCost));
  const fmt = (n) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "CLP",
      maximumFractionDigits: 0,
    }).format(n || 0);

  const resultingFare = (row) => {
    const amt = Math.round(num(row.amount));
    const signed = row.adjustmentType === "subtract" ? -amt : amt;
    return Math.max(0, def + signed);
  };

  // ¿La fila tiene filtro de vehículos y hay bases por vehículo para previsualizar?
  const rowHasVehicles = (row) =>
    vehicleFilter && Array.isArray(row.vehicleTypes) && row.vehicleTypes.length > 0;
  const canPreviewByVehicle = (row) =>
    rowHasVehicles(row) && vehicleBaseCosts && typeof vehicleBaseCosts === "object";

  // Tarifa resultante para un vehículo puntual: base del vehículo ± ajuste de la fila.
  const resultingFareForVehicle = (row, v) => {
    const base = Math.round(num(vehicleBaseCosts?.[v]));
    const amt = Math.round(num(row.amount));
    const signed = row.adjustmentType === "subtract" ? -amt : amt;
    return Math.max(0, base + signed);
  };

  // Sin cobertura conocida no se puede completar una regla nueva: se bloquea "Agregar"
  // (las filas existentes siguen editables y se pueden quitar).
  const canAddRow = coverageKnown && !noCoverage;
  const addDisabledReason = coverageLoading
    ? t("Cargando tu cobertura…")
    : !coverageKnown
      ? t("No se pudo cargar tu cobertura.")
      : t("Primero define tu cobertura.");

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-fg">
          {t("Tarifas por {{commune}}", { commune: communeLower })}
        </label>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="w-4 h-4" />}
          onClick={addRow}
          type="button"
          disabled={!canAddRow}
          title={canAddRow ? undefined : addDisabledReason}
        >
          {t("Agregar")}
        </Button>
      </div>
      <p className="text-xs text-fg-muted mb-2">
        {t("Suma o descuenta un monto fijo sobre la tarifa base por defecto según el {{commune}} de destino. Los que no tengan regla usan la tarifa base.", { commune: communeLower })}{" "}
        {t("Solo aparecen los destinos donde declaraste cobertura.")}
      </p>

      {/* Cobertura sin definir: dropdown vacío = callejón sin salida. Se explica y se
          enlaza la pestaña donde se define. */}
      {noCoverage && (
        <div className="rounded-md border border-border bg-bg-muted p-3 mb-2 flex items-start gap-2">
          <Globe className="w-4 h-4 text-fg-subtle mt-0.5 shrink-0" />
          <div className="text-xs text-fg-muted">
            <p className="font-medium text-fg mb-0.5">
              {t("Todavía no defines tu cobertura")}
            </p>
            <p>
              {t("Elige las {{regions}} y {{communes}} donde operas y después podrás tarifarlas acá.", {
                regions: pluralize(regionWord).toLowerCase(),
                communes: pluralize(communeWord).toLowerCase(),
              })}
            </p>
            <Link
              to={COVERAGE_TAB_PATH}
              className="text-brand-600 hover:underline font-medium"
            >
              {t("Ir a Cobertura")}
            </Link>
          </div>
        </div>
      )}

      {/* Cobertura desconocida (cargando o falló). No se filtra ni se marca nada: las
          reglas existentes se muestran tal cual para no sugerir que están mal. */}
      {!coverageKnown && (
        <p className="text-xs text-fg-muted mb-2">
          {coverageLoading
            ? t("Cargando tu cobertura…")
            : t("No se pudo cargar tu cobertura; recarga la página para volver a intentarlo.")}
        </p>
      )}

      {(!rows || rows.length === 0) && (
        <p className="text-xs text-fg-muted">
          {t("Sin reglas por {{commune}}; todo usa el monto por defecto.", { commune: communeLower })}
        </p>
      )}

      {(rows ?? []).map((row, i) => {
        const amt = Math.round(num(row.amount));
        const outOfCoverage = isOutOfCoverage(row);
        return (
          <div
            key={row.id ?? i}
            className={`flex flex-wrap items-end gap-2 mb-2 p-2 rounded-md border ${
              outOfCoverage
                ? "border-status-warning/50 bg-status-warning-soft"
                : "border-border/60"
            }`}
          >
            {/* Región (filtra la lista de comunas) */}
            <div className="flex flex-col gap-1 min-w-[140px] flex-1">
              <span className="text-xs text-fg-muted">{regionWord}</span>
              <SearchDropdown
                options={regionOptions}
                selected={regionLabel(row.regionId)}
                onChange={(v) => onRegionChange(i, v)}
                placeholder={loadingRegions ? t("Cargando…") : t("Seleccione…")}
                disabled={loadingRegions || regionOptions.length === 0}
                ariaLabel={`${t("Seleccionar")} ${regionLower}`}
              />
            </div>

            {/* Comuna. Fuera de cobertura la regla NO se oculta ni se descarta: se muestra
                en un campo de solo lectura con su aviso, porque esconderla la borraría en
                silencio al guardar (`zonesToApi` descarta las filas sin comuna). */}
            <div className="flex flex-col gap-1 min-w-[140px] flex-1">
              <span className="text-xs text-fg-muted">{communeWord}</span>
              {outOfCoverage ? (
                <span
                  className="h-9 inline-flex items-center px-2 rounded-md border border-status-warning/50 bg-bg text-sm text-fg truncate"
                  title={titleCase(row.commune)}
                >
                  {titleCase(row.commune)}
                </span>
              ) : (
                <SearchDropdown
                  options={communeOptions(row.regionId)}
                  selected={titleCase(row.commune)}
                  onChange={(v) => setRow(i, { commune: v })}
                  placeholder={communePlaceholder(row)}
                  disabled={!row.regionId}
                  ariaLabel={`${t("Seleccionar")} ${communeLower}`}
                />
              )}
            </div>

            {/* Sumar / Descontar */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">{t("Ajuste")}</span>
              <div className="inline-flex rounded-md border border-border overflow-hidden h-9">
                <button
                  type="button"
                  onClick={() => setRow(i, { adjustmentType: "add" })}
                  className={`px-3 text-sm ${
                    row.adjustmentType !== "subtract"
                      ? "bg-brand-500 text-white"
                      : "bg-bg text-fg-muted hover:text-fg"
                  }`}
                  title={t("Sumar a la tarifa base")}
                >
                  + {t("Sumar")}
                </button>
                <button
                  type="button"
                  onClick={() => setRow(i, { adjustmentType: "subtract" })}
                  className={`px-3 text-sm border-l border-border ${
                    row.adjustmentType === "subtract"
                      ? "bg-brand-500 text-white"
                      : "bg-bg text-fg-muted hover:text-fg"
                  }`}
                  title={t("Descontar de la tarifa base")}
                >
                  − {t("Descontar")}
                </button>
              </div>
            </div>

            {/* Monto */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">
                {t("Monto")} ({currency})
              </span>
              <input
                type="number"
                min="0"
                className={`${inputCls} max-w-[120px]`}
                value={row.amount}
                onChange={(e) => setRow(i, { amount: e.target.value })}
                placeholder={t("Ej: 500")}
              />
            </div>

            {/* Tarifa resultante (preview) */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">{t("Tarifa resultante")}</span>
              <span className="h-9 inline-flex items-center text-sm font-medium text-fg whitespace-nowrap">
                {canPreviewByVehicle(row) ? (
                  // Fila con vehículos específicos: resultado por vehículo (compacto).
                  row.vehicleTypes.map((v, idx) => (
                    <span key={v}>
                      {idx > 0 && <span className="text-fg-subtle"> · </span>}
                      <span className="text-fg-muted">{driverVehicleLabel(v, t)} </span>
                      {fmt(resultingFareForVehicle(row, v))}
                    </span>
                  ))
                ) : def > 0 ? (
                  fmt(resultingFare(row))
                ) : (
                  <span className="text-fg-muted">
                    {row.adjustmentType === "subtract" ? "−" : "+"}
                    {fmt(amt)} {t("s/ base")}
                  </span>
                )}
              </span>
            </div>

            <button
              type="button"
              className="h-9 px-2 text-fg-muted hover:text-status-error"
              onClick={() => removeRow(i)}
              aria-label={t("Quitar")}
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {outOfCoverage && (
              <p className="w-full flex items-start gap-1.5 text-[11px] text-status-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>
                  {t("Fuera de tu cobertura actual: esta regla se guarda y se sigue pagando. Para cambiarla, elige otra {{region}}; para dejar de usarla, quítala.", { region: regionLower })}
                </span>
              </p>
            )}

            {/* Filtro de vehículos (solo en modo diferenciado por vehículo). */}
            {vehicleFilter && (
              <div className="flex flex-col gap-1 w-full mt-1">
                <span className="text-xs text-fg-muted">{t("Vehículos")}</span>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    const selected = Array.isArray(row.vehicleTypes) ? row.vehicleTypes : [];
                    const allActive = selected.length === 0;
                    const chipCls = (active) =>
                      `h-7 px-2.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "border-brand-500 bg-brand-50 text-brand-700"
                          : "border-border text-fg-muted hover:bg-bg-muted"
                      }`;
                    return (
                      <>
                        <button
                          type="button"
                          className={chipCls(allActive)}
                          onClick={() => clearVehicles(i)}
                          title={t("Aplica a todos los vehículos")}
                        >
                          {t("Todos")}
                        </button>
                        {DRIVER_VEHICLE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className={chipCls(selected.includes(opt.value))}
                            onClick={() => toggleVehicle(i, opt.value)}
                          >
                            {t(opt.labelKey)}
                          </button>
                        ))}
                      </>
                    );
                  })()}
                </div>
                {rowHasVehicles(row) && (
                  <span className="text-[11px] text-fg-subtle">
                    {t("La regla específica de vehículo le gana a la genérica del mismo {{commune}}.", { commune: communeLower })}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
